<template>
  <base-page-layout>
    <div class="raffle-result-container">
      <div class="d-flex align-start justify-center">
        <mf-button text color="marca-0-l40" icon="arrow_back" label="Voltar" class="mr-2" @click="goBack(true)" />

        <div class="main-card" :style="`background: ${generateWinner.color}`">
          <img class="banner-image" :src="generateWinner.image_url" alt="" />

          <div class="center-content my-8">
            <div class="drawn-number">
              <div class="countdown d-flex align-center justify-center">
                <span :style="`color: ${countdownColor}`">{{ seconds }}</span>
              </div>
              <h1 class="text-center mb-3">Resultado do sorteio:</h1>
              <div v-if="currentSortedUser" class="drawn-item d-flex align-center" :style="`background: ${customReadableColor}`">
                <b class="mr-1">Nº {{ currentSortedUser.number }}</b>
                <span> - {{ currentSortedUser.name }}</span>
              </div>
              <div v-else class="empty-item" />

              <div class="d-flex justify-center">
                <mf-button
                  :disabled="isRaffling || currentIndex === allSortedItems.length || finishResult"
                  outlined
                  color="cinza-d10"
                  class="mt-4 mx-auto"
                  icon="casino"
                  label="Sortear ganhador"
                  @click="handleTimer"
                />
              </div>
            </div>
            <div class="px-4 drawn-table">
              <h1 class="text-center mb-3 last-winners">Últimos ganhadores:</h1>
              <v-data-table
                fixed-header
                disable-pagination
                hide-default-footer
                hide-default-header
                :headers="headers"
                height="200"
                :items="tableSortedItems"
                no-data-text="Sorteio em andamento..."
                :style="`background: ${customReadableColor}`"
              >
                <template v-slot:header="props">
                  <tr :style="`background: ${customReadableColor};`">
                    <th style="width: 90px">Número</th>
                    <th>Nome</th>
                  </tr>
                </template>
                <template v-slot:item="{ item, index }">
                  <tr :style="`background: ${customReadableColor}`">
                    <td>{{ item.number }}</td>
                    <td>{{ item.name }}</td>
                  </tr>
                </template>
              </v-data-table>

              <div class="d-flex justify-center">
                <mf-button label="Copiar para área de tranferência" icon="content_copy" outlined color="cinza-d10" class="mt-4" @click="copyToClipboard" />
              </div>
            </div>
          </div>

          <h2 class="mb-3">Informações</h2>
          <div class="d-flex align-center justify-center" style="gap: 8px">
            <div v-for="(card, index) in footerCards" :key="index" class="footer-info-card" :style="`background: ${customReadableColor}`">
              <div class="d-flex align-center">
                <mf-icon class="mr-1" :icon="card.icon" fill color="cinza-0" size="14" />
                <span>{{ card.label }}</span>
              </div>
              <b>{{ card.value }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
    <edit-winner-dialog v-if="isSavingWinner" is-from-raffler @close="closeEditWinnerDialog" @save="saveEditWinner" />
    <mf-action-buttons
      :primary-button="{
        text: 'Salvar ganhadores',
        action: saveWinners,
        isVisible: true,
        isDisabled: !finishResult || !hasPiiAccess,
      }"
    />
  </base-page-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import tinycolor from 'tinycolor2'
import BasePageLayout from '@/components/BasePageLayout.vue'
import { MUTATION_UPSERT_RAFFLE_WINNERS } from '../../graphql'
import EditWinnerDialog from '../components/EditWinnerDialog.vue'

export default {
  name: 'RaffleResult',
  components: { BasePageLayout, EditWinnerDialog },
  data: () => ({
    currentSortedItem: null,
    tableSortedItems: [],
    currentIndex: 0,
    seconds: 5,
    interval: null,
    tableDom: null,
    isRaffling: false,
    confettiTimeout: null,
    isSavingWinner: false,
  }),
  computed: {
    ...mapGetters(['generateWinner']),
    currentSortedUser: {
      get() {
        return this.currentSortedItem
      },
      set(value) {
        this.currentSortedItem = value
      },
    },
    numberOfWinners() {
      const { numberOfWinners } = this.generateWinner
      return numberOfWinners
    },
    allSortedItems() {
      return this.generateWinner.draw_winners
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
    footerCards() {
      const numbersLabel = this.numberOfWinners === 1 ? '1 número' : `${this.numberOfWinners} números`
      return [
        {
          icon: 'calendar_today',
          label: 'Data do sorteio',
          value: DateTime.fromFormat(this.generateWinner.selectedPrize.raffle_date, 'yyyy-mm-dd').toFormat('dd/mm/yyyy'),
        },
        {
          icon: 'sell',
          label: 'Quantidade sorteada',
          value: `${this.tableSortedItems.length}/${numbersLabel} da sorte`,
        },
        {
          icon: 'emoji_events',
          label: 'Prêmio',
          value: this.generateWinner.selectedPrize.title,
        },
      ]
    },
    finishResult() {
      return this.tableSortedItems.length === this.numberOfWinners
    },
    headers() {
      return [
        {
          text: 'Número',
          value: 'number',
          sortable: false,
          width: '90px',
        },
        {
          text: 'Nome',
          value: 'name',
          sortable: false,
          width: '',
        },
      ]
    },
    color() {
      return '#d7fcdc'
    },
    customReadableColor() {
      const { color } = this.generateWinner
      if (color === '#FFFFFFFF') return '#F6F6F8'
      return tinycolor(color).lighten(8).toString()
    },
    darkColor() {
      const { color } = this.generateWinner
      if (color === '#FFFFFFFF') return '#F6F6F8'
      return tinycolor(color).darken(30).toString()
    },
    countdownColor() {
      const { color } = this.generateWinner
      if (color === '#FFFFFFFF') return '#004D99'
      return this.darkColor
    },
  },
  beforeUnmount() {
    this.$confetti.stop()
  },
  methods: {
    goBack(fromRaffleResult) {
      const { raffle_id } = this.generateWinner
      this.$router.push({ path: `/mobile/sorteios/${raffle_id}`, query: { fromRaffleResult } })
    },

    copyToClipboard() {
      this.$confetti.stop()
      const clipboard = this.tableSortedItems.map((item) => item.number).join(';')
      navigator.clipboard.writeText(clipboard)
      this.$snackbar({ message: 'Copiado para área de transferencia', snackbarColor: 'success' })
    },

    clearInterval() {
      clearInterval(this.interval)
      this.interval = null
    },

    clearConfettiTimeout() {
      this.$confetti.stop()
      clearTimeout(this.confettiTimeout)
      this.confettiTimeout = null
    },

    handleTimer() {
      this.clearConfettiTimeout()
      this.isRaffling = true
      this.currentSortedUser = null
      this.clearInterval()
      this.seconds = 5
      this.interval = setInterval(() => {
        this.seconds--
        if (this.seconds === 0) this.handleNextWinner()
      }, 1000)
    },

    handleNextWinner() {
      this.clearInterval()
      const newItem = this.allSortedItems[this.currentIndex]
      this.currentIndex++
      this.currentSortedUser = { name: newItem.user.name, number: newItem.lucky_number.number }
      this.handleConfetti()
      const timeOut = setTimeout(() => {
        this.tableSortedItems.push(this.currentSortedUser)
        this.scrollToEnd()
        clearTimeout(timeOut)
        this.isRaffling = false
      }, 1000)
    },

    handleConfetti() {
      this.$confetti.start()
      this.confettiTimeout = setTimeout(() => {
        this.clearConfettiTimeout()
      }, 3000)
    },

    scrollToEnd() {
      this.$nextTick(() => {
        const table = document.querySelector('.v-data-table__wrapper')
        if (table) table.scrollTop = table?.scrollHeight
      })
    },
    saveEditWinner(notifications) {
      const { prize_id, raffle_id, draw_winners, selectedPrize } = this.generateWinner
      const winners = selectedPrize.winners

      const formattedWinners = draw_winners.map((draw) => {
        return {
          document_no: draw.user.document_no,
          user_id_erp: draw.user.id_erp,
          prize_given: false,
          notifications,
          lucky_number: {
            coupon_id: draw.lucky_number.coupon_id,
            created_at: draw.lucky_number.created_at,
            number: draw.lucky_number.number,
            unit_id_erp: draw.lucky_number.unit.id_erp,
          },
        }
      })

      if (Array.isArray(winners)) {
        formattedWinners.push(
          ...winners.map((winner) => {
            return {
              document_no: winner.user.document_no,
              user_id_erp: winner.user.id_erp,
              prize_given: winner.prize_given,
              notifications,
              lucky_number: {
                coupon_id: winner.lucky_number.coupon_id,
                created_at: winner.lucky_number.created_at,
                number: winner.lucky_number.number,
                unit_id_erp: winner.lucky_number.unit.id_erp,
              },
            }
          })
        )
      }

      this.$apollo
        .mutate({
          mutation: MUTATION_UPSERT_RAFFLE_WINNERS,
          variables: {
            raffleId: raffle_id,
            prizeId: prize_id,
            winners: formattedWinners,
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          if (response.data?.upsertWinners) {
            this.$snackbar({ message: 'Sucesso ao salvar ganhadores', snackbarColor: 'success' })
            this.goBack(false)
          }
        })
        .catch((err) => {
          console.log(err)
          this.$snackbar({ message: 'Erro ao salvar prêmio', snackbarColor: 'error' })
        })
    },
    saveWinners() {
      this.isSavingWinner = true
    },
    closeEditWinnerDialog() {
      this.isSavingWinner = false
    },
  },
}
</script>

<style lang="scss" scoped>
.raffle-result-container {
  .main-card {
    width: 100%;
    max-width: 992px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 48px;

    .banner-image {
      max-width: 100%;
      width: 100%;
      height: 228px;
      object-fit: cover;
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
    }

    h1 {
      color: $cinza-d20;
      font-size: 22px;
    }

    .last-winners {
      font-size: 18px;
    }

    h2 {
      font-weight: 400;
      color: $cinza-l10;
      font-size: 18px;
      text-align: center;
    }

    .footer-info-card {
      background-color: $cinza-l90;
      border-radius: 8px;
      padding: 16px 12px 16px 12px;
      width: 240px;

      span {
        font-size: 14px;
        color: $cinza-0;
        line-height: 20px;
      }

      b {
        font-weight: 600;
        font-size: 16px;
        line-height: 20px;
        color: $cinza-d10;
        margin-top: 8px;
      }
    }
  }

  .center-content {
    width: 100%;
    height: 290px;
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr 300px;

    .countdown {
      font-size: 80px;
      color: $marca-0-l20;
      text-align: center;
      font-weight: 700;
    }

    .empty-item {
      width: 100%;
      height: 51px;
      margin-bottom: 8px;
    }

    .drawn-item {
      border: 1px solid $cinza-d10;
      border-radius: 40px;
      padding: 8px 16px;
      margin: 0 auto 8px;
      animation: fade-in-grow 1s ease forwards;
      max-width: 410px;
      height: 51px;
      display: flex;
      justify-content: center;
      b {
        font-size: 22px;
        color: $cinza-0;
      }

      span {
        color: $cinza-d20;
        font-size: 22px;
        white-space: nowrap;
        max-width: 250px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
  padding: 0 8px;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
    padding: 0 8px;
  }
}

@keyframes fade-in-grow {
  0% {
    opacity: 0;
    transform: scale(0.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
